import { track } from "@/analytics/core";
import { pushDataLayer, pushDataLayerAndWait } from "@/analytics/gtm";
import { bucketCharCount, snakeCaseKeys } from "@/analytics/hygiene";
import { consumeIntent } from "@/analytics/intent";
import type {
  CanvasNodeSource,
  MessageInputMethod,
  MessageSurface,
  PaneMethod,
  SearchSource,
  TabMethod,
  UploadMethod,
} from "@/analytics/surfaces";
import { OrganizationUtm } from "@cohesive/api-client";

export enum AnalyticsEvent {
  APP_INSTALLED = "App Installed",
  AUDIO_RECORDING_STARTED = "Audio Recording Started",
  AUDIO_RECORDING_STOPPED = "Audio Recording Stopped",
  ACCESS_GRANTED = "Access Granted",
  ACCESS_CHANGED = "Access Changed",
  ACCESS_REVOKED = "Access Revoked",
  DESKTOP_APP_DOWNLOAD_CLICKED = "Desktop App Download Clicked",
  CHAT_RENAMED = "Chat Renamed",
  GROUP_CREATED = "Group Created",
  GROUP_DELETED = "Group Deleted",
  GROUP_MEMBER_ADDED = "Group Member Added",
  GROUP_MEMBER_REMOVED = "Group Member Removed",
  GROUP_OPENED = "Group Opened",
  GROUP_UPDATED = "Group Updated",
  LIGHT_DARK_MODE_TOGGLE = "Light / Dark Mode Toggle",
  LOGIN = "Login",
  LOGOUT = "Logout",
  MESSAGE_COPIED = "Message Copied",
  MESSAGE_FEEDBACK_SUBMITTED = "Message Feedback Submitted",
  MESSAGE_SENT = "Message Sent",
  MESSAGE_THUMBS_DOWN = "Message Disliked",
  MESSAGE_THUMBS_UP = "Message Liked",
  CHAT_CREATED = "Chat Created",
  CHAT_OPENED = "Chat Opened",
  CHAT_ARCHIVED = "Chat Archived",
  CHAT_RESTORED = "Chat Restored",
  CHAT_STARRED = "Chat Starred",
  CHAT_UNSTARRED = "Chat Unstarred",
  GENERATION_STOPPED = "Generation Stopped",
  COMPOSER_CONTROL_CHANGED = "Composer Control Changed",
  COMPOSER_ATTACHMENT_ADDED = "Composer Attachment Added",
  // Object lifecycle outcomes (fired from mutation hooks' onSuccess)
  WORKSPACE_CREATED = "Workspace Created",
  WORKSPACE_RENAMED = "Workspace Renamed",
  WORKSPACE_ARCHIVED = "Workspace Archived",
  WORKSPACE_RESTORED = "Workspace Restored",
  CANVAS_CREATED = "Canvas Created",
  CANVAS_RENAMED = "Canvas Renamed",
  CANVAS_MOVED = "Canvas Moved",
  CANVAS_ARCHIVED = "Canvas Archived",
  CANVAS_RESTORED = "Canvas Restored",
  CANVAS_DUPLICATED = "Canvas Duplicated",
  FOLDER_CREATED = "Folder Created",
  FILE_RENAMED = "File Renamed",
  FILE_MOVED = "File Moved",
  FILE_COPIED = "File Copied",
  FILE_ARCHIVED = "File Archived",
  FILE_RESTORED = "File Restored",
  FILE_PUBLISHED = "File Published",
  FILE_UNPUBLISHED = "File Unpublished",
  FILE_VERSION_RESTORED = "File Version Restored",
  FAVORITE_ADDED = "Favorite Added",
  FAVORITE_REMOVED = "Favorite Removed",
  AUTOMATION_CREATED = "Automation Created",
  AUTOMATION_EDITED = "Automation Edited",
  AUTOMATION_DELETED = "Automation Deleted",
  AUTOMATION_DUPLICATED = "Automation Duplicated",
  AUTOMATION_TOGGLED = "Automation Toggled",
  AUTOMATION_RUN_NOW_CLICKED = "Automation Run Now Clicked",
  FLOATING_CHAT_OPENED = "Floating Chat Opened",
  FLOATING_CHAT_OPENED_AS_TAB = "Floating Chat Opened As Tab",
  FLOATING_CHAT_MINIMIZED = "Floating Chat Minimized",
  ORGANIZATION_CREATED = "Organization Created",
  TEMPLATE_SIGNUP_STARTED = "Template Signup Started",
  TEMPLATE_PROMPT_SHOWN = "Template Prompt Shown",
  TEMPLATE_PROMPT_DISMISSED = "Template Prompt Dismissed",
  TEMPLATE_COPY_COMPLETED = "Template Copy Completed",
  TEMPLATE_COPY_FAILED = "Template Copy Failed",
  USER_CONNECTION_AUTHORIZED = "User Connection Authorized",
  DISCOVERABLE_CONNECTION_ACCESS_REQUESTED = "Discoverable Connection Access Requested",
  USER_INVITED = "User Invited",
  USER_PROFILE_EDITED = "User Profile Edited",
  SKILL_CREATED = "Skill Created",
  SKILL_ARCHIVED = "Skill Archived",
  SKILL_RESTORED = "Skill Restored",
  SKILL_PUBLISHED = "Skill Published",
  SKILL_EDITED = "Skill Edited",
  FOLLOW_UP_CLICKED = "Follow Up Clicked",
  SAVE_INTEGRATION_CONNECTION_INSTRUCTIONS = "Save Integration Connection Instructions",
  CONNECTOR_ENTITY_SELECTED = "Connector Entity Selected",
  SKILL_SELECTED = "Skill Selected",
  // Intent and transfer
  OBJECT_ACTION_INVOKED = "Object Action Invoked",
  TRANSFER_COMPLETED = "Transfer Completed",
  TRANSFER_REJECTED = "Transfer Rejected",
  // Container views
  TAB_OPENED = "Tab Opened",
  TAB_SWITCHED = "Tab Switched",
  TAB_CLOSED = "Tab Closed",
  TAB_REORDERED = "Tab Reordered",
  TAB_RENAMED = "Tab Renamed",
  PANE_OPENED = "Pane Opened",
  PANE_CLOSED = "Pane Closed",
  RAIL_SELECTED = "Rail Selected",
  RAIL_TOGGLED = "Rail Toggled",
  SETTINGS_OPENED = "Settings Opened",
  SETTINGS_SCOPE_VIEWED = "Settings Scope Viewed",
  HOME_BROWSER_TAB_CHANGED = "Home Browser Tab Changed",
  HOME_BROWSER_VIEW_CHANGED = "Home Browser View Changed",
  HOME_BROWSER_SORT_CHANGED = "Home Browser Sort Changed",
  HOME_BROWSER_SEARCHED = "Home Browser Searched",
  SEARCH_OPENED = "Search Opened",
  SEARCH_PERFORMED = "Search Performed",
  SEARCH_RESULT_OPENED = "Search Result Opened",
  SEARCH_CLOSED = "Search Closed",
  NOTIFICATIONS_OPENED = "Notifications Opened",
  NOTIFICATION_CLICKED = "Notification Clicked",
  NOTIFICATIONS_MARKED_READ = "Notifications Marked Read",
  // Files and canvas UI
  FILE_UPLOAD_STARTED = "File Upload Started",
  FILE_UPLOAD_COMPLETED = "File Upload Completed",
  FILE_UPLOAD_FAILED = "File Upload Failed",
  FILE_OPENED = "File Opened",
  FILE_DOWNLOADED = "File Downloaded",
  FILE_VERSION_HISTORY_OPENED = "File Version History Opened",
  FILE_TEXT_SENT_TO_CHAT = "File Text Sent To Chat",
  CANVAS_NODE_ADDED = "Canvas Node Added",
  CANVAS_NODE_REMOVED = "Canvas Node Removed",
  CANVAS_EDGE_CONNECTED = "Canvas Edge Connected",
  CANVAS_EDGE_REMOVED = "Canvas Edge Removed",
  CANVAS_FIT_VIEW = "Canvas Fit View",
  CANVAS_TOOL_SELECTED = "Canvas Tool Selected",
  CANVAS_SHORTCUT_USED = "Canvas Shortcut Used",
  CANVAS_QUICK_CHAT_STARTED = "Canvas Quick Chat Started",
  CANVAS_QUICK_VOICE_STARTED = "Canvas Quick Voice Started",
  // Sharing and growth
  SHARE_DIALOG_OPENED = "Share Dialog Opened",
  PUBLIC_LINK_TOGGLED = "Public Link Toggled",
  PUBLIC_LINK_COPIED = "Public Link Copied",
  PUBLIC_LINK_ACCESS_CHANGED = "Public Link Access Changed",
  ONBOARDING_WELCOME_STARTED = "Onboarding Welcome Started",
  ONBOARDING_WELCOME_SKIPPED = "Onboarding Welcome Skipped",
  COACHMARK_SHOWN = "Coachmark Shown",
  COACHMARK_COMPLETED = "Coachmark Completed",
  COACHMARK_SKIPPED = "Coachmark Skipped",
  FIRST_RUN_STARTED = "First Run Started",
  FIRST_RUN_SENT = "First Run Sent",
  FIRST_RUN_WANTS = "First Run Wants",
  FIRST_RUN_PRIMER_BEAT = "First Run Primer Beat",
  FIRST_RUN_LANDED = "First Run Landed",
  FIRST_RUN_COACH = "First Run Coach",
  FIRST_RUN_TILE_OPENED = "First Run Tile Opened",
  FIRST_RUN_TILE_CANCELLED = "First Run Tile Cancelled",
  PUBLIC_CANVAS_ADD_TO_WORKSPACE_CLICKED = "Public Canvas Add To Workspace Clicked",
  PUBLIC_CANVAS_ADD_TO_WORKSPACE_CONFIRMED = "Public Canvas Add To Workspace Confirmed",
  // Billing Events
  BILLING_PLAN_SELECTED = "Billing Plan Selected",
  BILLING_PLAN_CONFIGURED = "Billing Plan Configured",
  BILLING_ENTERPRISE_INQUIRY_CLICKED = "Billing Enterprise Inquiry Clicked",
  ENTERPRISE_INQUIRY_SUBMITTED = "Enterprise Inquiry Submitted",
  BILLING_CHECKOUT_SUBMITTED = "Billing Checkout Submitted",
  BILLING_CHECKOUT_COMPLETED = "Billing Checkout Completed",
  BILLING_CHECKOUT_FAILED = "Billing Checkout Failed",
  BILLING_SUBSCRIPTION_CANCELLED = "Billing Subscription Cancelled",
  BILLING_SUBSCRIPTION_REACTIVATED = "Billing Subscription Reactivated",
  BILLING_PAYMENT_METHOD_UPDATE_STARTED = "Billing Payment Method Update Started",
  BILLING_BUDGET_UPDATED = "Billing Budget Updated",
  BILLING_EMAIL_UPDATED = "Billing Email Updated",
  BILLING_INVOICE_DOWNLOADED = "Billing Invoice Downloaded",
  BILLING_INVOICE_VIEWED = "Billing Invoice Viewed",
  BILLING_TRIAL_PAYMENT_PROMPT_CLICKED = "Billing Trial Payment Prompt Clicked",
  BILLING_USAGE_LIMIT_ACTION_CLICKED = "Billing Usage Limit Action Clicked",
  BILLING_MANAGE_CLICKED = "Billing Manage Clicked",
  BILLING_PLAN_CHANGE_CLICKED = "Billing Plan Change Clicked",
}

// Sends through the guarded core; a no-op unless initAnalytics() ran.
const sendAnalyticsEvent = (
  eventName: AnalyticsEvent,
  properties: Record<string, any>,
): void => {
  track(eventName, snakeCaseKeys(properties));
};

/** last_intent_* pairs consumed from the intent register. */
export type OutcomeIntent = Record<string, string>;

// Outcome events additionally carry last_intent_* from the intent register,
// tying the mutation back to the menu/drag that asked for it. A mutation
// that emits more than one outcome consumes the intent once and passes it
// to each tracker — consumeIntent is single-shot, so the second emit would
// otherwise lose it.
const sendOutcomeEvent = (
  eventName: AnalyticsEvent,
  properties: Record<string, any>,
  intent?: OutcomeIntent,
): void => {
  track(eventName, {
    ...snakeCaseKeys(properties),
    ...(intent ?? consumeIntent()),
  });
};

// Event Tracking Functions
export const trackDesktopAppDownloadClicked = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.DESKTOP_APP_DOWNLOAD_CLICKED, {});
};

export const trackConnectorEntitySelected = ({
  platform,
  entityType,
  surface,
}: {
  platform: string;
  entityType: string;
  surface: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CONNECTOR_ENTITY_SELECTED, {
    platform,
    entityType,
    surface,
  });
};

export const trackSkillSelected = ({
  skillId,
  surface,
}: {
  skillId: string;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SKILL_SELECTED, { skillId, surface });
};

export const trackAudioRecordingStarted = ({
  surface,
  mode,
}: { surface?: string; mode?: string } = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.AUDIO_RECORDING_STARTED, {
    surface,
    mode,
  });
};

export const trackAudioRecordingStopped = ({
  durationSeconds,
  wasAutoStopped = false,
  surface,
  mode,
}: {
  durationSeconds: number;
  wasAutoStopped?: boolean;
  surface?: string;
  mode?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.AUDIO_RECORDING_STOPPED, {
    durationSeconds,
    wasAutoStopped,
    surface,
    mode,
  });
};

interface AccessChangeProps {
  objectKind: string;
  subjectKind: string;
  role: string;
  count?: number;
}

export const trackAccessGranted = (props: AccessChangeProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.ACCESS_GRANTED, { count: 1, ...props });
};

export const trackAccessChanged = (props: AccessChangeProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.ACCESS_CHANGED, { count: 1, ...props });
};

export const trackAccessRevoked = (props: AccessChangeProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.ACCESS_REVOKED, { count: 1, ...props });
};

// The single Message Sent emitter — fired once per successful send from
// useMessageActions, whatever the surface. Content never leaves the client;
// only a length bucket does.
export const trackMessageSent = ({
  surface,
  inputMethod,
  charCount = 0,
  attachmentCount = 0,
  mentionCount = 0,
  mentionTypes,
  skillCount = 0,
  harness,
  model,
  reasoningEffort,
  confirmationMode,
  sessionClass,
  sessionMode,
  isNewSession,
  flowSessionId,
  canvasId,
  workspaceId,
}: {
  surface: MessageSurface;
  inputMethod: MessageInputMethod;
  charCount?: number;
  attachmentCount?: number;
  mentionCount?: number;
  mentionTypes?: string[];
  skillCount?: number;
  harness?: string;
  model?: string;
  reasoningEffort?: string;
  confirmationMode?: string;
  sessionClass?: string;
  sessionMode?: string;
  isNewSession: boolean;
  flowSessionId: string;
  canvasId?: string;
  workspaceId?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_SENT, {
    surface,
    inputMethod,
    charCountBucket: bucketCharCount(charCount),
    attachmentCount,
    mentionCount,
    mentionTypes,
    skillCount,
    harness,
    model,
    reasoningEffort,
    confirmationMode,
    sessionClass,
    sessionMode,
    isNewSession,
    flowSessionId,
    canvasId,
    workspaceId,
  });
};

export const trackChatCreated = ({
  flowSessionId,
  sessionClass,
  mode,
  harness,
  hasCanvas,
  workspaceId,
}: {
  flowSessionId: string;
  sessionClass?: string;
  mode?: string;
  harness?: string;
  hasCanvas: boolean;
  workspaceId?: string;
}): void => {
  sendOutcomeEvent(AnalyticsEvent.CHAT_CREATED, {
    flowSessionId,
    sessionClass,
    mode,
    harness,
    hasCanvas,
    workspaceId,
  });
};

export const trackChatOpened = ({
  source,
  mode,
}: {
  source: string;
  mode: "floating" | "tab" | "pane";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CHAT_OPENED, { source, mode });
};

interface ChatEventProps {
  flowSessionId?: string;
  surface?: string;
}

export const trackChatRenamed = (props: ChatEventProps = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CHAT_RENAMED, { ...props });
};

export const trackChatArchived = (props: ChatEventProps = {}): void => {
  sendOutcomeEvent(AnalyticsEvent.CHAT_ARCHIVED, { ...props });
};

export const trackChatRestored = (props: ChatEventProps = {}): void => {
  sendOutcomeEvent(AnalyticsEvent.CHAT_RESTORED, { ...props });
};

export const trackChatStarred = (props: ChatEventProps = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CHAT_STARRED, { ...props });
};

export const trackChatUnstarred = (props: ChatEventProps = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CHAT_UNSTARRED, { ...props });
};

export const trackGenerationStopped = ({
  flowSessionId,
  surface,
}: {
  flowSessionId?: string;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.GENERATION_STOPPED, {
    flowSessionId,
    surface,
  });
};

export const trackComposerControlChanged = ({
  control,
  value,
  surface,
}: {
  control: "harness" | "model" | "reasoning_effort" | "confirmation_mode";
  value: string;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.COMPOSER_CONTROL_CHANGED, {
    control,
    value,
    surface,
  });
};

export const trackComposerAttachmentAdded = ({
  method,
  count,
  surface,
}: {
  method: "picker" | "drop_os" | "drop_in_app" | "paste";
  count: number;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.COMPOSER_ATTACHMENT_ADDED, {
    method,
    count,
    surface,
  });
};

interface GroupEventProps {
  groupId: string;
  memberCount?: number;
}

export const trackGroupCreated = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_CREATED, { ...props });
};

export const trackGroupDeleted = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_DELETED, { ...props });
};

export const trackGroupMemberAdded = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_MEMBER_ADDED, { ...props });
};

export const trackGroupMemberRemoved = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_MEMBER_REMOVED, { ...props });
};

export const trackGroupOpened = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_OPENED, { ...props });
};

export const trackGroupUpdated = (props: GroupEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.GROUP_UPDATED, { ...props });
};

export const trackFloatingChatOpened = ({
  source,
  workspaceScoped,
  sizeClass,
  canvasId,
}: {
  source: string;
  workspaceScoped: boolean;
  sizeClass?: string;
  canvasId?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_OPENED, {
    source,
    workspaceScoped,
    sizeClass,
    canvasId,
  });
};

export const trackFloatingChatOpenedAsTab = ({
  canvasId,
}: { canvasId?: string } = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_OPENED_AS_TAB, { canvasId });
};

export const trackFloatingChatMinimized = ({
  canvasId,
}: { canvasId?: string } = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FLOATING_CHAT_MINIMIZED, { canvasId });
};

export const trackLightDarkModeToggle = ({
  mode,
}: {
  mode: "light" | "dark";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.LIGHT_DARK_MODE_TOGGLE, { mode });
};

export const trackLogin = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.LOGIN, {});
  pushDataLayer("login");
};

export const trackLogout = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.LOGOUT, {});
};

export const trackMessageCopied = ({
  flowSessionId,
  messageId,
  surface,
}: {
  flowSessionId?: string;
  messageId?: string;
  surface?: string;
} = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_COPIED, {
    flowSessionId,
    messageId,
    surface,
  });
};

// Payload exception (approved): feedback text + message content stay.
export const trackMessageFeedbackSubmitted = ({
  feedback,
  messageId,
  messageContent,
  flowSessionId,
}: {
  feedback: string;
  messageId: string;
  messageContent: string;
  flowSessionId?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_FEEDBACK_SUBMITTED, {
    feedback,
    messageId,
    messageContent,
    flowSessionId,
  });
};

interface MessageVoteProps {
  flowSessionId?: string;
  messageId?: string;
  surface?: string;
}

export const trackMessageThumbsDown = (props: MessageVoteProps = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_THUMBS_DOWN, { ...props });
};

export const trackMessageThumbsUp = (props: MessageVoteProps = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.MESSAGE_THUMBS_UP, { ...props });
};

// source: "client" is future-proofing — if a server emitter ever takes
// this over, reports can dedupe by origin during the overlap and the client
// call is then removed (name moves to SERVER_OWNED_EVENT_NAMES). The GTM
// sign_up push stays client-side either way.
//
// Resolves once the GTM tags on `sign_up` have fired (or a bounded wait has
// elapsed): both callers navigate to Auth0 right after, and an immediate
// unload was cutting off the Ads conversion request. The hashed email rides
// along for enhanced conversions, so Ads can still match the signup when the
// click cookie did not survive the round trip. Callers hash it (gtm.ts
// hashEmailForAds): the raw address never enters this module.
export const trackOrganizationCreated = async ({
  organizationId,
  signupSource,
  utm,
  sha256Email,
}: {
  organizationId: string;
  signupSource?: string;
  utm?: OrganizationUtm | null;
  sha256Email?: string | null;
}): Promise<void> => {
  sendAnalyticsEvent(AnalyticsEvent.ORGANIZATION_CREATED, {
    organizationId,
    source: "client",
    ...(signupSource ? { signupSource } : {}),
    utm_source: utm?.source,
    utm_medium: utm?.medium,
    utm_campaign: utm?.campaign,
    utm_content: utm?.content,
    utm_term: utm?.term,
  });

  await pushDataLayerAndWait("sign_up", {
    organization_id: organizationId,
    ...(sha256Email ? { sha256_email_address: sha256Email } : {}),
  });
};

export const trackTemplateSignupStarted = ({
  kind,
  cta,
  id,
}: {
  kind: string;
  cta: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_SIGNUP_STARTED, { kind, cta, id });
};

export const trackTemplatePromptShown = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_PROMPT_SHOWN, { kind, id });
};

export const trackTemplatePromptDismissed = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_PROMPT_DISMISSED, { kind, id });
};

export const trackTemplateCopyCompleted = ({
  kind,
  id,
}: {
  kind: string;
  id: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_COPY_COMPLETED, { kind, id });
};

export const trackTemplateCopyFailed = ({
  kind,
  id,
  reason,
}: {
  kind: string;
  id: string;
  reason: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TEMPLATE_COPY_FAILED, { kind, id, reason });
};

export const trackSkillCreated = ({
  skillId,
  isTemplate,
  source,
}: {
  skillId: string;
  isTemplate?: boolean;
  source?: "scratch" | "zip" | "template";
}): void => {
  sendOutcomeEvent(AnalyticsEvent.SKILL_CREATED, {
    skillId,
    isTemplate,
    source,
  });
};

export const trackSkillEdited = ({ skillId }: { skillId: string }): void => {
  sendOutcomeEvent(AnalyticsEvent.SKILL_EDITED, { skillId });
};

export const trackSkillArchived = ({ skillId }: { skillId: string }): void => {
  sendOutcomeEvent(AnalyticsEvent.SKILL_ARCHIVED, { skillId });
};

export const trackSkillRestored = ({ skillId }: { skillId: string }): void => {
  sendOutcomeEvent(AnalyticsEvent.SKILL_RESTORED, { skillId });
};

export const trackSkillPublished = ({ skillId }: { skillId: string }): void => {
  sendOutcomeEvent(AnalyticsEvent.SKILL_PUBLISHED, { skillId });
};

// Workspace outcomes
interface WorkspaceEventProps {
  workspaceId: string;
  isPrivate?: boolean;
}

export const trackWorkspaceCreated = (props: WorkspaceEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.WORKSPACE_CREATED, { ...props });
};

export const trackWorkspaceRenamed = (props: WorkspaceEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.WORKSPACE_RENAMED, { ...props });
};

export const trackWorkspaceArchived = (props: WorkspaceEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.WORKSPACE_ARCHIVED, { ...props });
};

export const trackWorkspaceRestored = (props: WorkspaceEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.WORKSPACE_RESTORED, { ...props });
};

// Canvas outcomes
interface CanvasEventProps {
  canvasId: string;
  workspaceId?: string;
  crossWorkspace?: boolean;
  fromTemplate?: boolean;
  /** Pass when one mutation emits several outcomes (consumeIntent once). */
  intent?: OutcomeIntent;
}

export const trackCanvasCreated = ({
  intent,
  ...props
}: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_CREATED, { ...props }, intent);
};

export const trackCanvasRenamed = ({
  intent,
  ...props
}: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_RENAMED, { ...props }, intent);
};

export const trackCanvasMoved = ({
  intent,
  ...props
}: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_MOVED, { ...props }, intent);
};

export const trackCanvasArchived = (props: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_ARCHIVED, { ...props });
};

export const trackCanvasRestored = (props: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_RESTORED, { ...props });
};

export const trackCanvasDuplicated = (props: CanvasEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.CANVAS_DUPLICATED, { ...props });
};

// File-record outcomes. object_kind distinguishes file/folder; class is the
// file-record class (document, upload, …); cascade marks bulk deletes from
// the folder/board cascade helpers.
interface FileEventProps {
  fileRecordId?: string;
  objectKind?: "file" | "folder";
  class?: string;
  extension?: string;
  destinationClass?: string;
  count?: number;
  cascade?: boolean;
}

export const trackFolderCreated = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FOLDER_CREATED, { ...props });
};

export const trackFileRenamed = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_RENAMED, { ...props });
};

export const trackFileMoved = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_MOVED, { ...props });
};

export const trackFileCopied = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_COPIED, { ...props });
};

export const trackFileArchived = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_ARCHIVED, { ...props });
};

export const trackFileRestored = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_RESTORED, { ...props });
};

export const trackFilePublished = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_PUBLISHED, { ...props });
};

export const trackFileUnpublished = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_UNPUBLISHED, { ...props });
};

export const trackFileVersionRestored = (props: FileEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.FILE_VERSION_RESTORED, { ...props });
};

export const trackFavoriteAdded = ({
  objectKind,
  objectId,
  surface,
}: {
  objectKind: string;
  objectId: string;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FAVORITE_ADDED, {
    objectKind,
    objectId,
    surface,
  });
};

export const trackFavoriteRemoved = ({
  objectKind,
  objectId,
  surface,
}: {
  objectKind: string;
  objectId: string;
  surface?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FAVORITE_REMOVED, {
    objectKind,
    objectId,
    surface,
  });
};

// Automation (flow schedule) outcomes
interface AutomationEventProps {
  scheduleId: string;
  resourceType?: string;
  harness?: string;
  hasCron?: boolean;
  enabled?: boolean;
  /** Edited only: which patch keys changed. */
  editedFields?: string[];
}

export const trackAutomationCreated = (props: AutomationEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.AUTOMATION_CREATED, { ...props });
};

export const trackAutomationEdited = (props: AutomationEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.AUTOMATION_EDITED, { ...props });
};

export const trackAutomationDeleted = (props: AutomationEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.AUTOMATION_DELETED, { ...props });
};

export const trackAutomationDuplicated = (
  props: AutomationEventProps,
): void => {
  sendOutcomeEvent(AnalyticsEvent.AUTOMATION_DUPLICATED, { ...props });
};

export const trackAutomationToggled = (props: AutomationEventProps): void => {
  sendOutcomeEvent(AnalyticsEvent.AUTOMATION_TOGGLED, { ...props });
};

export const trackAutomationRunNowClicked = (
  props: AutomationEventProps,
): void => {
  sendAnalyticsEvent(AnalyticsEvent.AUTOMATION_RUN_NOW_CLICKED, { ...props });
};

export const trackUserConnectionAuthorized = ({
  integrationId,
  platform,
  provider,
}: {
  integrationId?: string;
  platform: string;
  provider: "nango" | "pipedream" | "mcp";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_CONNECTION_AUTHORIZED, {
    integrationId,
    platform,
    provider,
  });
};

export const trackDiscoverableConnectionAccessRequested = ({
  integrationId,
  platform,
}: {
  integrationId: string;
  platform: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.DISCOVERABLE_CONNECTION_ACCESS_REQUESTED, {
    integrationId,
    platform,
  });
};

// If a server emitter takes invites over, remove this and reserve the name
// in SERVER_OWNED_EVENT_NAMES.
export const trackUserInvited = ({
  roles,
  count,
  sameDomain,
}: {
  roles: string[];
  count: number;
  sameDomain: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_INVITED, {
    roles,
    count,
    sameDomain,
  });
};

export const trackUserProfileEdited = ({
  editedFields,
}: {
  editedFields: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.USER_PROFILE_EDITED, {
    editedFields,
  });
};

export const trackFollowUpClicked = ({
  flowSessionId,
  position,
}: {
  flowSessionId: string;
  position?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FOLLOW_UP_CLICKED, {
    flowSessionId,
    position,
  });
};

export const trackSaveIntegrationConnectionInstructions = ({
  integrationConnectionId,
  instructionsLength,
}: {
  integrationConnectionId: string;
  instructionsLength: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SAVE_INTEGRATION_CONNECTION_INSTRUCTIONS, {
    integrationConnectionId,
    instructionsLengthBucket: bucketCharCount(instructionsLength),
  });
};

export const trackAppInstalled = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.APP_INSTALLED, {});
};

// Billing Events
export const trackBillingPlanSelected = ({
  planName,
  billingInterval,
  seatCount,
  source,
  organization_id,
}: {
  planName: string;
  billingInterval?: string;
  seatCount?: number;
  source: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_SELECTED, {
    planName,
    billingInterval,
    seatCount,
    source,
    organization_id,
  });
};

export const trackBillingPlanConfigured = ({
  planName,
  billingInterval,
  seatCount,
  hasPaymentMethod,
  organization_id,
}: {
  planName: string;
  billingInterval: string;
  seatCount: number;
  hasPaymentMethod: boolean;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_CONFIGURED, {
    planName,
    billingInterval,
    seatCount,
    hasPaymentMethod,
    organization_id,
  });
};

export const trackBillingEnterpriseInquiryClicked = ({
  source,
  organization_id,
}: {
  source: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_ENTERPRISE_INQUIRY_CLICKED, {
    source,
    organization_id,
  });
};

export const trackEnterpriseInquirySubmitted = ({
  companySize,
  industry,
  organization_id,
}: {
  companySize: string;
  industry: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.ENTERPRISE_INQUIRY_SUBMITTED, {
    companySize,
    industry,
    organization_id,
  });

  pushDataLayer("contact_sales");
};

export const trackBillingCheckoutSubmitted = ({
  planName,
  billingInterval,
  seatCount,
  isPaymentMethodUpdate,
  organization_id,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  isPaymentMethodUpdate: boolean;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_SUBMITTED, {
    planName,
    billingInterval,
    seatCount,
    isPaymentMethodUpdate,
    organization_id,
  });
};

export const trackBillingCheckoutCompleted = ({
  planName,
  billingInterval,
  seatCount,
  isPaymentMethodUpdate,
  wasTrialing,
  organization_id,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  isPaymentMethodUpdate: boolean;
  wasTrialing: boolean;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_COMPLETED, {
    planName,
    billingInterval,
    seatCount,
    isPaymentMethodUpdate,
    wasTrialing,
    organization_id,
  });

  if (!isPaymentMethodUpdate) {
    pushDataLayer("subscription_start");
  }
};

export const trackBillingCheckoutFailed = ({
  planName,
  billingInterval,
  seatCount,
  error,
  isPaymentMethodUpdate,
  organization_id,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  error: string;
  isPaymentMethodUpdate: boolean;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_CHECKOUT_FAILED, {
    planName,
    billingInterval,
    seatCount,
    error,
    isPaymentMethodUpdate,
    organization_id,
  });
};

export const trackBillingSubscriptionCancelled = ({
  planName,
  billingInterval,
  seatCount,
  organization_id,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_SUBSCRIPTION_CANCELLED, {
    planName,
    billingInterval,
    seatCount,
    organization_id,
  });
};

export const trackBillingSubscriptionReactivated = ({
  planName,
  billingInterval,
  seatCount,
  organization_id,
}: {
  planName?: string;
  billingInterval?: string;
  seatCount?: number;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_SUBSCRIPTION_REACTIVATED, {
    planName,
    billingInterval,
    seatCount,
    organization_id,
  });
};

export const trackBillingPaymentMethodUpdateStarted = ({
  source,
  currentPlan,
  organization_id,
}: {
  source: string;
  currentPlan?: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PAYMENT_METHOD_UPDATE_STARTED, {
    source,
    currentPlan,
    organization_id,
  });
};

export const trackBillingBudgetUpdated = ({
  budgetAmount,
  isUnlimited,
  planName,
  organization_id,
}: {
  budgetAmount?: number;
  isUnlimited: boolean;
  planName?: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_BUDGET_UPDATED, {
    budgetAmount,
    isUnlimited,
    planName,
    organization_id,
  });
};

export const trackBillingEmailUpdated = ({
  planName,
  organization_id,
}: {
  planName?: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_EMAIL_UPDATED, {
    planName,
    organization_id,
  });
};

export const trackBillingInvoiceDownloaded = ({
  invoiceId,
  invoiceAmount,
  invoiceStatus,
  organization_id,
}: {
  invoiceId: string;
  invoiceAmount: number;
  invoiceStatus: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_INVOICE_DOWNLOADED, {
    invoiceId,
    invoiceAmount,
    invoiceStatus,
    organization_id,
  });
};

export const trackBillingInvoiceViewed = ({
  invoiceId,
  invoiceAmount,
  invoiceStatus,
  organization_id,
}: {
  invoiceId: string;
  invoiceAmount: number;
  invoiceStatus: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_INVOICE_VIEWED, {
    invoiceId,
    invoiceAmount,
    invoiceStatus,
    organization_id,
  });
};

export const trackBillingTrialPaymentPromptClicked = ({
  promptType,
  daysUntilTrialEnd,
  usageReason,
  organization_id,
}: {
  promptType: string;
  daysUntilTrialEnd?: number;
  usageReason?: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_TRIAL_PAYMENT_PROMPT_CLICKED, {
    promptType,
    daysUntilTrialEnd,
    usageReason,
    organization_id,
  });
};

export const trackBillingUsageLimitActionClicked = ({
  limitReason,
  actionType,
  organization_id,
}: {
  limitReason: string;
  actionType: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_USAGE_LIMIT_ACTION_CLICKED, {
    limitReason,
    actionType,
    organization_id,
  });
};

export const trackBillingManageClicked = ({
  source,
  organization_id,
}: {
  source: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_MANAGE_CLICKED, {
    source,
    organization_id,
  });
};

export const trackBillingPlanChangeClicked = ({
  currentPlan,
  source,
  organization_id,
}: {
  currentPlan?: string;
  source: string;
  organization_id?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.BILLING_PLAN_CHANGE_CLICKED, {
    currentPlan,
    source,
    organization_id,
  });
};

// GTM only; deliberately independent of the Mixpanel flag. Not named
// `session_start`: that is a GA4 reserved, automatically collected event, and
// forwarding a manual one mid-session strips the session's traffic source.
// The GTM trigger listens for `app_session_start`.
export const trackSessionStart = ({ userId }: { userId: string }): void => {
  pushDataLayer("app_session_start", { user_id: userId });
};

// Intent seam: one event per object-action menu/toolbar/context invocation.
export const trackObjectActionInvoked = ({
  action,
  objectKind,
  objectSubtype,
  surface,
  scope,
  presentation,
  selectionCount,
  format,
}: {
  action: string;
  objectKind?: string;
  objectSubtype?: string;
  surface?: string;
  scope?: string;
  presentation?: string;
  selectionCount?: number;
  format?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.OBJECT_ACTION_INVOKED, {
    action,
    objectKind,
    objectSubtype,
    surface,
    scope,
    presentation,
    selectionCount,
    format,
  });
};

interface TransferEventProps {
  payloadKind?: string;
  destinationKind?: string;
  surface?: string;
  action?: string;
  count?: number;
  isSharedSource?: boolean;
}

export const trackTransferCompleted = (props: TransferEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.TRANSFER_COMPLETED, { ...props });
};

export const trackTransferRejected = (props: TransferEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.TRANSFER_REJECTED, { ...props });
};

// Container views
export const trackTabOpened = ({
  tabKind,
  openTabCount,
}: {
  tabKind: string;
  openTabCount: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TAB_OPENED, { tabKind, openTabCount });
};

export const trackTabSwitched = ({ tabKind }: { tabKind?: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.TAB_SWITCHED, { tabKind });
};

export const trackTabClosed = ({
  tabKind,
  method,
  count,
}: {
  tabKind?: string;
  method: TabMethod;
  count?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TAB_CLOSED, { tabKind, method, count });
};

export const trackTabReordered = ({
  fromIndex,
  toIndex,
}: {
  fromIndex: number;
  toIndex: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.TAB_REORDERED, { fromIndex, toIndex });
};

export const trackTabRenamed = ({ tabKind }: { tabKind?: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.TAB_RENAMED, { tabKind });
};

export const trackPaneOpened = ({
  paneType,
  method,
  source,
  layout,
}: {
  paneType?: string;
  method: PaneMethod;
  source?: string;
  layout?: "pane" | "full_route";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PANE_OPENED, {
    paneType,
    method,
    source,
    layout,
  });
};

export const trackPaneClosed = ({
  paneType,
  method,
}: {
  paneType?: string;
  method: PaneMethod;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PANE_CLOSED, { paneType, method });
};

export const trackRailSelected = ({
  rail,
  source,
}: {
  rail: string;
  source?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.RAIL_SELECTED, { rail, source });
};

export const trackRailToggled = ({
  open,
  source,
}: {
  open: boolean;
  source?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.RAIL_TOGGLED, { open, source });
};

export const trackSettingsOpened = ({
  tab,
  scope,
  source,
}: {
  tab?: string;
  scope?: string;
  source?: "ui" | "deep_link" | "quick_action";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SETTINGS_OPENED, { tab, scope, source });
};

export const trackSettingsScopeViewed = ({
  tab,
  scope,
}: {
  tab?: string;
  scope?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SETTINGS_SCOPE_VIEWED, { tab, scope });
};

interface HomeBrowserEventProps {
  surface: "home" | "workspace";
}

export const trackHomeBrowserTabChanged = ({
  tab,
  surface,
}: HomeBrowserEventProps & { tab: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.HOME_BROWSER_TAB_CHANGED, {
    tab,
    surface,
  });
};

export const trackHomeBrowserViewChanged = ({
  viewMode,
  wide,
  surface,
}: HomeBrowserEventProps & { viewMode: string; wide?: boolean }): void => {
  sendAnalyticsEvent(AnalyticsEvent.HOME_BROWSER_VIEW_CHANGED, {
    viewMode,
    wide,
    surface,
  });
};

export const trackHomeBrowserSortChanged = ({
  field,
  direction,
  surface,
}: HomeBrowserEventProps & { field: string; direction: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.HOME_BROWSER_SORT_CHANGED, {
    field,
    direction,
    surface,
  });
};

export const trackHomeBrowserSearched = ({
  queryLength,
  resultCount,
  surface,
}: HomeBrowserEventProps & {
  queryLength: number;
  resultCount?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.HOME_BROWSER_SEARCHED, {
    queryLength,
    resultCount,
    surface,
  });
};

// Rail search. Search Performed keeps raw query (approved exception).
export const trackSearchOpened = ({
  source,
}: {
  source: SearchSource;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SEARCH_OPENED, { source });
};

export const trackSearchPerformed = ({
  query,
  resultCount,
  resultTypes,
}: {
  query: string;
  resultCount: number;
  resultTypes?: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SEARCH_PERFORMED, {
    query,
    queryLength: query.length,
    resultCount,
    resultTypes,
    hasResults: resultCount > 0,
  });
};

export const trackSearchResultOpened = ({
  resultType,
  position,
  method,
}: {
  resultType: string;
  position: number;
  method: "click" | "keyboard" | "drag";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SEARCH_RESULT_OPENED, {
    resultType,
    position,
    method,
  });
};

export const trackSearchClosed = ({
  hadQuery,
  selectedResult,
}: {
  hadQuery: boolean;
  selectedResult: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SEARCH_CLOSED, {
    hadQuery,
    selectedResult,
  });
};

export const trackNotificationsOpened = ({
  unreadCount,
}: {
  unreadCount?: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.NOTIFICATIONS_OPENED, { unreadCount });
};

export const trackNotificationClicked = ({ kind }: { kind?: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.NOTIFICATION_CLICKED, { kind });
};

export const trackNotificationsMarkedRead = ({
  count,
}: {
  count?: number;
} = {}): void => {
  sendAnalyticsEvent(AnalyticsEvent.NOTIFICATIONS_MARKED_READ, { count });
};

// File uploads and viewers
interface FileUploadEventProps {
  count?: number;
  sizeBucket?: string;
  destinationClass?: string;
  method?: UploadMethod;
}

export const trackFileUploadStarted = (props: FileUploadEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_UPLOAD_STARTED, { ...props });
};

export const trackFileUploadCompleted = (props: FileUploadEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_UPLOAD_COMPLETED, { ...props });
};

export const trackFileUploadFailed = (props: FileUploadEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_UPLOAD_FAILED, { ...props });
};

export const trackFileOpened = ({
  fileRecordId,
  kind,
  class: fileClass,
  extension,
  viewer,
  surface,
}: {
  fileRecordId: string;
  kind?: string;
  class?: string;
  extension?: string;
  viewer?: string;
  surface?: "pane" | "full_route" | "public";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_OPENED, {
    fileRecordId,
    kind,
    class: fileClass,
    extension,
    viewer,
    surface,
  });
};

export const trackFileDownloaded = ({
  fileRecordId,
  format,
  extension,
}: {
  fileRecordId?: string;
  format?: string;
  extension?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_DOWNLOADED, {
    fileRecordId,
    format,
    extension,
  });
};

export const trackFileVersionHistoryOpened = ({
  fileRecordId,
}: {
  fileRecordId: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_VERSION_HISTORY_OPENED, {
    fileRecordId,
  });
};

export const trackFileTextSentToChat = ({
  fileRecordId,
  extension,
}: {
  fileRecordId: string;
  extension?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FILE_TEXT_SENT_TO_CHAT, {
    fileRecordId,
    extension,
  });
};

// Canvas UI (user-performed operations only; pan/zoom deliberately excluded)
export const trackCanvasNodeAdded = ({
  nodeType,
  source,
  canvasId,
}: {
  nodeType?: string;
  source: CanvasNodeSource;
  canvasId?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_NODE_ADDED, {
    nodeType,
    source,
    canvasId,
  });
};

export const trackCanvasNodeRemoved = ({
  count,
  nodeTypes,
}: {
  count: number;
  nodeTypes?: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_NODE_REMOVED, {
    count,
    nodeTypes,
  });
};

export const trackCanvasEdgeConnected = ({
  edgeType,
}: {
  edgeType?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_EDGE_CONNECTED, { edgeType });
};

export const trackCanvasEdgeRemoved = ({ count }: { count: number }): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_EDGE_REMOVED, { count });
};

export const trackCanvasFitView = ({
  source,
}: {
  source: "toolbar" | "keyboard";
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_FIT_VIEW, { source });
};

export const trackCanvasToolSelected = ({ tool }: { tool: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_TOOL_SELECTED, { tool });
};

export const trackCanvasShortcutUsed = ({
  shortcut,
}: {
  shortcut: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_SHORTCUT_USED, { shortcut });
};

export const trackCanvasQuickChatStarted = ({
  canvasId,
  source,
}: {
  canvasId?: string;
  source?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_QUICK_CHAT_STARTED, {
    canvasId,
    source,
  });
};

export const trackCanvasQuickVoiceStarted = ({
  canvasId,
  source,
}: {
  canvasId?: string;
  source?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.CANVAS_QUICK_VOICE_STARTED, {
    canvasId,
    source,
  });
};

// Sharing and growth
export const trackShareDialogOpened = ({
  objectKind,
  source,
}: {
  objectKind?: string;
  source?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.SHARE_DIALOG_OPENED, {
    objectKind,
    source,
  });
};

export const trackPublicLinkToggled = ({
  objectKind,
  on,
  accessLevel,
}: {
  objectKind: string;
  on: boolean;
  accessLevel?: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PUBLIC_LINK_TOGGLED, {
    objectKind,
    on,
    accessLevel,
  });
};

export const trackPublicLinkCopied = ({
  objectKind,
}: {
  objectKind: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PUBLIC_LINK_COPIED, { objectKind });
};

export const trackPublicLinkAccessChanged = ({
  objectKind,
  accessLevel,
}: {
  objectKind: string;
  accessLevel: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PUBLIC_LINK_ACCESS_CHANGED, {
    objectKind,
    accessLevel,
  });
};

export const trackOnboardingWelcomeStarted = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_WELCOME_STARTED, {});
};

export const trackOnboardingWelcomeSkipped = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.ONBOARDING_WELCOME_SKIPPED, {});
};

/** One coachmark card: the moment and card keys, the 1-based step, the count. */
export interface CoachmarkEventProps {
  moment: string;
  card: string;
  step: number;
  count: number;
}

export const trackCoachmarkShown = (props: CoachmarkEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.COACHMARK_SHOWN, props);
};

export const trackCoachmarkCompleted = (props: CoachmarkEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.COACHMARK_COMPLETED, props);
};

export const trackCoachmarkSkipped = (props: CoachmarkEventProps): void => {
  sendAnalyticsEvent(AnalyticsEvent.COACHMARK_SKIPPED, props);
};

export const trackFirstRunStarted = (): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_STARTED, {});
};

/** `attached` counts the files that rode the send, the job's samples included. */
export const trackFirstRunSent = ({
  brief,
  attached,
}: {
  brief: string;
  attached: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_SENT, { brief, attached });
};

/** What the second question got: the picked keys, or none. */
export const trackFirstRunWants = ({
  brief,
  wants,
}: {
  brief: string;
  wants: string[];
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_WANTS, {
    brief,
    wants: wants.length ? wants : "none",
  });
};

export const trackFirstRunPrimerBeat = ({ n }: { n: number }): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_PRIMER_BEAT, { n });
};

export const trackFirstRunLanded = ({
  primerBeats,
  primerSkipped,
  landing,
}: {
  primerBeats: number;
  primerSkipped: boolean;
  landing: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_LANDED, {
    primerBeats,
    primerSkipped,
    landing,
  });
};

/** A tile's light box opened or was cancelled; `tile` is the brief key or "custom". */
export const trackFirstRunTileOpened = ({ tile }: { tile: string }): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_TILE_OPENED, { tile });
};
export const trackFirstRunTileCancelled = ({
  tile,
}: {
  tile: string;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_TILE_CANCELLED, { tile });
};

/** How the landing's coachmarks ended, and the card they ended on (0-based). */
export const trackFirstRunCoach = ({
  how,
  card,
}: {
  how: "done" | "skip" | "left";
  card: number;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.FIRST_RUN_COACH, { how, card });
};

export const trackPublicCanvasAddToWorkspaceClicked = ({
  canvasId,
  authenticated,
}: {
  canvasId: string;
  authenticated: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PUBLIC_CANVAS_ADD_TO_WORKSPACE_CLICKED, {
    canvasId,
    authenticated,
  });
};

export const trackPublicCanvasAddToWorkspaceConfirmed = ({
  canvasId,
  destinationPrivate,
}: {
  canvasId: string;
  destinationPrivate?: boolean;
}): void => {
  sendAnalyticsEvent(AnalyticsEvent.PUBLIC_CANVAS_ADD_TO_WORKSPACE_CONFIRMED, {
    canvasId,
    destinationPrivate,
  });
};

export default {
  trackAppInstalled,
  trackLogin,
  trackLogout,
  trackAccessGranted,
  trackAccessChanged,
  trackAccessRevoked,
  trackChatRenamed,
  trackGroupCreated,
  trackGroupDeleted,
  trackGroupMemberAdded,
  trackGroupMemberRemoved,
  trackGroupOpened,
  trackGroupUpdated,
  trackLightDarkModeToggle,
  trackMessageCopied,
  trackMessageFeedbackSubmitted,
  trackMessageThumbsDown,
  trackMessageThumbsUp,
  trackOrganizationCreated,
  trackTemplateSignupStarted,
  trackTemplatePromptShown,
  trackTemplatePromptDismissed,
  trackTemplateCopyCompleted,
  trackTemplateCopyFailed,
  trackSkillCreated,
  trackSkillEdited,
  trackUserConnectionAuthorized,
  trackDiscoverableConnectionAccessRequested,
  trackUserInvited,
  trackUserProfileEdited,
  trackFollowUpClicked,
  trackSaveIntegrationConnectionInstructions,
  trackConnectorEntitySelected,
  trackSkillSelected,
  trackAudioRecordingStarted,
  trackAudioRecordingStopped,
  // Billing Events
  trackBillingPlanSelected,
  trackBillingPlanConfigured,
  trackBillingEnterpriseInquiryClicked,
  trackEnterpriseInquirySubmitted,
  trackBillingCheckoutSubmitted,
  trackBillingCheckoutCompleted,
  trackBillingCheckoutFailed,
  trackBillingSubscriptionCancelled,
  trackBillingSubscriptionReactivated,
  trackBillingPaymentMethodUpdateStarted,
  trackBillingBudgetUpdated,
  trackBillingEmailUpdated,
  trackBillingInvoiceDownloaded,
  trackBillingInvoiceViewed,
  trackBillingTrialPaymentPromptClicked,
  trackBillingUsageLimitActionClicked,
  trackBillingManageClicked,
  trackBillingPlanChangeClicked,
  trackSessionStart,
};
